/* ============================================================================
   language-gate.js
   Shows the language chooser once per visitor, then remembers the choice.
   Switching language posts Shopify's own /localization form, so Markets
   routing, currency and the locale URL prefix all stay correct.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'fitcheck:language-gate';

  function read() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function write(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* private browsing — the gate simply shows again next time */
    }
  }

  function clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function LanguageGate(root) {
    this.root = root;
    this.form = root.querySelector('[data-language-gate-form]');
    this.input = root.querySelector('[data-language-gate-input]');
    this.options = Array.prototype.slice.call(root.querySelectorAll('[data-language-gate-option]'));
    this.isEditor = root.dataset.editor === 'true';
    this.rememberChoice = root.dataset.remember !== 'false';
    this.previouslyFocused = null;
    this.sectionId = root.dataset.sectionId || '';

    this.onKeydown = this.onKeydown.bind(this);
    this.bind();
    this.maybeOpen();
  }

  LanguageGate.prototype.bind = function () {
    var self = this;

    this.options.forEach(function (button) {
      button.addEventListener('click', function () {
        self.choose(button);
      });
    });

    var dismissers = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-language-gate-dismiss], [data-language-gate-close]')
    );
    dismissers.forEach(function (button) {
      button.addEventListener('click', function () {
        /* Closing counts as "I've seen this" — do not nag on the next page. */
        if (!self.isEditor) self.remember();
        self.close();
      });
    });

    /* Anything on the page can reopen the chooser. */
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-language-gate-open]');
      if (!trigger) return;
      event.preventDefault();
      self.open();
    });
  };

  LanguageGate.prototype.maybeOpen = function () {
    /* Inside the theme editor the gate must never take over the screen on its
       own — that would trap the merchant on this section and block editing
       anything else. It opens only while the section is actually selected in
       the sidebar (see the shopify:section:select listener at the bottom). */
    if (this.isEditor) return;
    if (this.rememberChoice && read()) return;
    this.open();
  };

  LanguageGate.prototype.open = function () {
    if (this.root.dataset.visible === 'true') return;
    this.previouslyFocused = document.activeElement;
    this.root.hidden = false;
    this.root.dataset.closing = 'false';

    /* Force a reflow so the opening transition runs. */
    void this.root.offsetWidth;
    this.root.dataset.visible = 'true';

    /* The lock goes on <html>, never on <body>: making <body> a scroll
       container breaks position:sticky on the header. And in the editor we
       never lock at all, so the preview stays scrollable. */
    if (!this.isEditor) {
      document.documentElement.classList.add('language-gate-open');
    }
    document.addEventListener('keydown', this.onKeydown);

    var first = this.options[0];
    if (first && !this.isEditor) {
      window.setTimeout(function () {
        first.focus();
      }, 320);
    }
  };

  LanguageGate.prototype.close = function () {
    var self = this;
    if (this.root.dataset.visible !== 'true') return;
    this.root.dataset.closing = 'true';
    this.root.dataset.visible = 'false';
    document.documentElement.classList.remove('language-gate-open');
    document.removeEventListener('keydown', this.onKeydown);

    window.setTimeout(function () {
      self.root.hidden = true;
      self.root.dataset.closing = 'false';
    }, 450);

    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  };

  LanguageGate.prototype.remember = function () {
    if (this.rememberChoice) write('1');
  };

  LanguageGate.prototype.choose = function (button) {
    var iso = button.getAttribute('data-language-gate-option');
    var current = this.root.dataset.currentLocale;

    this.remember();

    /* Already on that language — just get out of the way. */
    if (!iso || iso === current) {
      this.close();
      return;
    }

    if (this.isEditor) {
      this.close();
      return;
    }

    button.setAttribute('aria-busy', 'true');
    this.input.value = iso;
    this.form.submit();
  };

  /* Keep focus inside the gate while it is open. */
  LanguageGate.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!this.isEditor) this.remember();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = this.root.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function init() {
    var root = document.querySelector('[data-language-gate]');
    if (!root) return;
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    window.FitCheckLanguageGate = new LanguageGate(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* ---------------------------------------------------------------------
     Theme editor wiring.
     The gate shows only while its own section is selected, and hides again
     as soon as the merchant clicks any other section. Without this the fixed
     overlay would cover the preview permanently and there would be no way
     back to the home page sections.
     --------------------------------------------------------------------- */
  function gateFor(target) {
    if (!target) return null;
    var el = target.querySelector ? target.querySelector('[data-language-gate]') : null;
    return el;
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (gateFor(event.target)) init();
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (!gateFor(event.target)) return;
    if (window.FitCheckLanguageGate) window.FitCheckLanguageGate.open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (!gateFor(event.target)) return;
    if (window.FitCheckLanguageGate) window.FitCheckLanguageGate.close();
  });

  /* Safety net: if the editor ever leaves the lock class behind, drop it. */
  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.classList.remove('language-gate-open');
  });

  window.FitCheckLanguageGateReset = clear;
})();
